import React from 'react';
import { motion } from '@/lib/motion';
import { ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { getGeneratedImage } from '../utils/mediaUrl';

export const DualNatureSection = () => {
  const navigate = useNavigate();
  
  const industrialImage = getGeneratedImage('dual_industrial');
  const artisticImage = getGeneratedImage('dual_artistic');

  return (
    <section id="dual-nature" className="relative py-14 md:py-20 bg-gallery overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Statement */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-bronze text-[9px] md:text-[10px] font-body font-semibold uppercase tracking-[0.2em] mb-4">
              <span className="w-5 h-px bg-bronze" />
              OUR PHILOSOPHY
            </span>
            
            <h2 className="font-display text-2xl md:text-3xl font-bold text-obsidian uppercase tracking-tight leading-tight mb-4">
              THE DUAL NATURE OF
              <br />
              <span className="text-bronze">METAL MASTERY</span>
            </h2>
            
            <p className="font-body text-[14px] md:text-[15px] text-concrete leading-relaxed mb-5 max-w-lg">
              We operate at the intersection of industrial precision and artistic expression. 
              Our workshop is both a high-tech fabrication facility and an artist's studio.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="p-4 bg-white border border-obsidian/5">
                <div className="font-display text-[10px] md:text-[11px] font-bold text-obsidian uppercase tracking-[0.12em] mb-1.5">
                  INDUSTRIAL PRECISION
                </div>
                <p className="font-body text-[12px] md:text-[13px] text-concrete leading-relaxed">
                  ISO certified, CNC capabilities, engineering excellence.
                </p>
              </div>
              <div className="p-4 bg-obsidian text-white">
                <div className="font-display text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] mb-1.5 text-bronze">
                  ARTISTIC VISION
                </div>
                <p className="font-body text-[12px] md:text-[13px] text-white/70 leading-relaxed">
                  Collaborative design, master finishes, sculptural work.
                </p>
              </div>
            </div>

            <motion.button
              onClick={() => navigate('/about')}
              className="inline-flex items-center gap-2 font-body text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] text-obsidian hover:text-bronze transition-colors group"
              whileHover={{ x: 5 }}
            >
              LEARN OUR STORY
              <ArrowRight weight="bold" className="w-3 h-3 transition-transform group-hover:translate-x-1" />
            </motion.button>
          </motion.div>

          {/* Right: Visual Grid */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 gap-2 md:gap-3"
          >
            {/* Industrial Image */}
            <div className="aspect-[3/4] overflow-hidden">
              <img loading="lazy" decoding="async"
                src={industrialImage}
                alt="Precision CNC laser cutting in Dubai facility"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                width="400"
                height="533"
              />
            </div>
            
            {/* Artistic Image - offset */}
            <div className="aspect-[3/4] overflow-hidden mt-6 md:mt-8">
              <img loading="lazy" decoding="async"
                src={artisticImage}
                alt="Master craftsman finishing bronze sculptural panel"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                width="400"
                height="533"
              />
            </div>

            {/* Stats Bar */}
            <div className="col-span-2 bg-obsidian p-4 flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="font-display text-lg md:text-xl font-bold text-white">90,000</div>
                <div className="text-[8px] md:text-[9px] font-body font-semibold uppercase tracking-[0.1em] text-white/50">SQ.FT</div>
              </div>
              <div className="h-8 w-px bg-white/20" />
              <div className="text-center flex-1">
                <div className="font-display text-lg md:text-xl font-bold text-white">260+</div>
                <div className="text-[8px] md:text-[9px] font-body font-semibold uppercase tracking-[0.1em] text-white/50">CRAFTSMEN</div>
              </div>
              <div className="h-8 w-px bg-white/20" />
              <div className="text-center flex-1">
                <div className="font-display text-lg md:text-xl font-bold text-bronze">ISO</div>
                <div className="text-[8px] md:text-[9px] font-body font-semibold uppercase tracking-[0.1em] text-white/50">CERTIFIED</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
